/**
 * Fundbox Fuse — React hook for in-page embed lifecycle.
 *
 * Wires page CTAs to launchFundboxFuseEmbed (fundboxFuseClient.ts) when the mount
 * node is in the DOM: open embed view → load/init FbxFuse → client.open().
 *
 * Vendor fuse.js can fail the iframe asynchronously (e.g. "Window did not load after
 * 5000ms") without rejecting open(). We watch for onStateReady and treat a missing
 * ready signal as failure so the page can leave the blank mount and offer retry.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import useFundboxAuthCode from '@/enterprise/data/useFundboxAuthCode';
import { launchFundboxFuseEmbed, resetFundboxFuseClient } from '@/enterprise/features/fundbox/fuse/fundboxFuseClient';
import { getFundboxFuseConfig } from '@/enterprise/features/fundbox/fuse/fundboxFuseConfig';
import useDataDog from '@/reporting/DataDog';

/** Attribute fuse.js reads off the mount div when injecting the authenticated iframe. */
const FUNDBOX_AUTH_TOKEN_ATTRIBUTE = 'data-fundbox-auth-token';

export type FundboxFuseEmbedStatus = 'idle' | 'loading' | 'ready' | 'error';

export const isFundboxFuseEmbedOpen = (status: FundboxFuseEmbedStatus): boolean =>
  status === 'loading' || status === 'ready';

interface UseFundboxFuseEmbedResult {
  embedStatus: FundboxFuseEmbedStatus;
  fuseConfig: ReturnType<typeof getFundboxFuseConfig>;
  mountRef: (node: HTMLDivElement | null) => void;
  openEmbed: () => void;
  closeEmbed: () => void;
}

function useFundboxFuseEmbed(): UseFundboxFuseEmbedResult {
  const fuseConfig = useMemo(() => getFundboxFuseConfig(), []);
  const { fetchAuthCode } = useFundboxAuthCode();
  const { addError, addAction } = useDataDog();
  const [embedStatus, setEmbedStatus] = useState<FundboxFuseEmbedStatus>('idle');
  const [mountNode, setMountNode] = useState<HTMLDivElement | null>(null);

  const teardownEmbed = useCallback((): void => {
    resetFundboxFuseClient();
    setMountNode(null);
  }, []);

  const closeEmbed = useCallback((): void => {
    teardownEmbed();
    setEmbedStatus('idle');
  }, [teardownEmbed]);

  const failEmbed = useCallback(
    (error: unknown, reason: string): void => {
      addError(error, { feature: 'fundbox-fuse', reason });
      teardownEmbed();
      setEmbedStatus('error');
    },
    [addError, teardownEmbed]
  );

  const openEmbed = useCallback((): void => {
    if (!fuseConfig) {
      return;
    }

    if (embedStatus === 'error') {
      addAction('fundbox-fuse::refetch-after-error', { feature: 'fundbox-fuse' });
    }

    setEmbedStatus('loading');
  }, [addAction, embedStatus, fuseConfig]);

  const mountRef = useCallback((node: HTMLDivElement | null): void => {
    setMountNode(node);
  }, []);

  useEffect(() => {
    if (embedStatus !== 'loading' || !fuseConfig || !mountNode) {
      return;
    }

    let cancelled = false;
    let watchdogId: number | undefined;

    const clearWatchdog = (): void => {
      if (watchdogId !== undefined) {
        window.clearTimeout(watchdogId);
        watchdogId = undefined;
      }
    };

    const launchFuse = async (): Promise<void> => {
      try {
        // Block launch on the per-user auth code so fuse.js reads it off the mount div when
        // it injects the iframe. On failure we degrade to the unauthenticated signup flow
        // rather than blocking the user, and surface the failure in DataDog for monitoring.
        if (fuseConfig.oauthClientId) {
          try {
            const authCode = await fetchAuthCode(fuseConfig.oauthClientId);

            if (cancelled) {
              return;
            }

            mountNode.setAttribute(FUNDBOX_AUTH_TOKEN_ATTRIBUTE, authCode);
          } catch (error) {
            addError(error, { feature: 'fundbox-fuse', reason: 'auth-code-fetch-failed' });
          }
        }

        if (cancelled) {
          return;
        }

        const client = await launchFundboxFuseEmbed({
          scriptUrl: fuseConfig.scriptUrl,
          partnerId: fuseConfig.partnerId,
          container: mountNode,
          onStateReady: () => {
            if (cancelled) {
              return;
            }

            clearWatchdog();
            setEmbedStatus('ready');
          },
          onStateChange: () => undefined,
        });

        if (cancelled) {
          return;
        }

        // Slightly longer than Fuse’s ~5s iframe timeout so we react after the vendor failure.
        watchdogId = window.setTimeout(() => {
          failEmbed(new Error('Fundbox Fuse iframe did not become ready'), 'iframe-load-timeout');
        }, 6_000);

        client.open();
      } catch (error) {
        if (!cancelled) {
          failEmbed(error, 'launch-failed');
        }
      }
    };

    void launchFuse();

    return () => {
      cancelled = true;
      clearWatchdog();
    };
  }, [addError, failEmbed, fetchAuthCode, fuseConfig, embedStatus, mountNode]);

  return {
    embedStatus,
    fuseConfig,
    mountRef,
    openEmbed,
    closeEmbed,
  };
}

export default useFundboxFuseEmbed;
